import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useCommentCount = (blogPostId: string) => {
  const [commentCount, setCommentCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCommentCount = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_public_comments', { post_id: blogPostId });

      if (error) throw error;

      setCommentCount(data?.length || 0);
    } catch (error: any) {
      console.error('Error fetching comment count:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommentCount();
  }, [blogPostId]);

  // Subscribe to real-time changes for this blog post's comments
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    
    const channel = supabase
      .channel('comment-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `blog_post_id=eq.${blogPostId}`
        },
        () => {
          fetchCommentCount();
        }
      )
      .subscribe((status) => {
        // Silently handle connection errors and fallback to polling
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          // Fallback to polling every 30 seconds if realtime fails
          if (!pollInterval) {
            pollInterval = setInterval(fetchCommentCount, 30000);
          }
        }
      });

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      supabase.removeChannel(channel);
    };
  }, [blogPostId]);

  return {
    commentCount,
    loading,
    refetch: fetchCommentCount
  };
};